import { ReactNode, useMemo, useState, useCallback, useEffect } from 'react';
import DeltaDataContext from '@raas/shared/src/contexts/DeltaDataContext';
import DeltaDocumentDataContext from '@raas/shared/src/contexts/DeltaDataDocumentContext';
import DeltaUIContext from '@raas/shared/src/contexts/DeltaUIContext';
import DeltaDocumentUIContext from '@raas/shared/src/contexts/DeltaDocumentUIContext';
import axios from '@raas/shared/src/utils/axios';
import regulationsService from '@raas/shared/src/service/api/RegulationsService';
import documentElementService from '@raas/shared/src/service/api/DocumentElementsService';
import legalProvisionService from '@raas/shared/src/service/api/LegalProvisionsService';
import { BASE_HEADERS, COMPARE_HEADERS } from '@raas/shared/src/routes/paths';
import type {
  ILegalProvisionDeltaDetails,
  IReadLegalProvision,
} from '@raas/shared/src/types/legal-provision';
import type {
  IGetLegalProvisionDetailsParams,
  IDeltaTranslation,
} from '@raas/shared/src/types/delta';
import {
  IDocumentElementDelta,
  IReadDocumentElement,
  BodyChanged,
} from '@raas/shared/src/types/document-element';

type DeltaContextProviderProps = {
  children: ReactNode;
};

export function DeltaContextProvider({ children }: DeltaContextProviderProps) {
  const [baseDetails, setBaseDetails] = useState<IReadLegalProvision | null>(null);
  const [compareDetails, setCompareDetails] = useState<IReadLegalProvision | null>(null);

  const [showMoreDetails, setShowMoreDetails] = useState<boolean>(true);

  const [showOnlyUpdatedParagraphs, setShowOnlyUpdatedParagraphs] = useState<boolean>(true);
  const [shouldBeRefresh, setShouldBeRefresh] = useState<boolean>(false);
  const [isLoadingDelta, setIsLoadingDelta] = useState<boolean>(false);
  const [delta, setDelta] = useState<ILegalProvisionDeltaDetails | null>(null);
  const [currentDeltaTranslation, setCurrentDeltaTranslation] = useState<IDeltaTranslation | null>(
    null
  );

  const deltaTranslations = useMemo(() => {
    if (!baseDetails || !compareDetails) {
      return [];
    }
    const translations: IDeltaTranslation[] = [];
    baseDetails.translations.forEach((baseTranslation) => {
      const compareTranslation = compareDetails.translations.find(
        (compTranslation) => compTranslation.language === baseTranslation.language
      );
      if (compareTranslation) {
        translations.push({
          language: baseTranslation.language,
          base: baseTranslation,
          compare: compareTranslation,
        });
      }
    });
    return [...translations].sort((a, b) => {
      if (a.base.isDefault !== b.base.isDefault) {
        return a.base.isDefault ? -1 : 1;
      }
      return a.language.localeCompare(b.language);
    });
  }, [baseDetails, compareDetails]);

  useEffect(() => {
    const defaultEntry = deltaTranslations.find((t) => t.base.isDefault);
    setCurrentDeltaTranslation(defaultEntry ?? null);
  }, [deltaTranslations]);

  const [documentElementDetails, setDocumentElementDetails] = useState<IReadDocumentElement | null>(
    null
  );

  const [showOnlyPotencialCandidates, setShowOnlyPotencialCandidates] = useState<boolean>(true);
  const [shouldBeRefreshCandidates, setShouldBeRefreshCandidates] = useState<boolean>(true);
  const [isLoadingPlacementCandidates, setIsLoadingPlacementCandidates] = useState<boolean>(false);
  const [legalProvisionPlacementCandidates, setLegalProvisionPlacementCandidates] =
    useState<ILegalProvisionDeltaDetails | null>(null);

  const getLegalProvisionDetails = useCallback(
    async (params: IGetLegalProvisionDetailsParams): Promise<IReadLegalProvision> => {
      const { id, entityId, versionId, versionTimestamp, releaseTimestamp } = params;
      axios.defaults.headers.common[BASE_HEADERS.EntityId] = entityId;
      axios.defaults.headers.common[BASE_HEADERS.VersionId] = versionId;
      axios.defaults.headers.common[BASE_HEADERS.VersionTimestamp] = versionTimestamp;
      if (releaseTimestamp) {
        axios.defaults.headers.common[BASE_HEADERS.ReleaseTimestamp] = releaseTimestamp;
      }

      return regulationsService.get(id);
    },
    []
  );

  const fetchDocumentElement = useCallback(
    async (documentElementId: string): Promise<IReadDocumentElement> => {
      if (!baseDetails) {
        throw new Error('Base details must be set to fetch document element details');
      }
      const { entityId, versionId, versionTimestamp, releaseTimestamp } = baseDetails;
      axios.defaults.headers.common[BASE_HEADERS.EntityId] = entityId;
      axios.defaults.headers.common[BASE_HEADERS.VersionId] = versionId;
      axios.defaults.headers.common[BASE_HEADERS.VersionTimestamp] = versionTimestamp.toISOString();
      if (releaseTimestamp) {
        axios.defaults.headers.common[BASE_HEADERS.ReleaseTimestamp] =
          releaseTimestamp.toISOString();
      } else {
        axios.defaults.headers.common[BASE_HEADERS.ReleaseTimestamp] = '';
      }
      return documentElementService.get(documentElementId);
    },
    [baseDetails]
  );

  const showOlyUpdatedDocumentElements = useCallback(
    (elements: IDocumentElementDelta[]): IDocumentElementDelta[] =>
      elements
        .filter((elem) => {
          if (elem?.changeStatus !== BodyChanged.NOT_CHANGED) {
            return true;
          }

          const hasChangedChildren = elem.children?.some(
            (child) =>
              child?.changeStatus !== BodyChanged.NOT_CHANGED ||
              showOlyUpdatedDocumentElements([child]).length > 0
          );

          return !!hasChangedChildren;
        })
        .map((elem) => ({
          ...elem,
          children: showOlyUpdatedDocumentElements(elem.children),
        })),
    []
  );

  const fetchDeltaDetails = useCallback(async () => {
    if (baseDetails && compareDetails) {
      setIsLoadingDelta(true);
      const result = await legalProvisionService.getDelta(
        baseDetails.id,
        compareDetails.id,
        currentDeltaTranslation?.base?.translationId,
        currentDeltaTranslation?.compare?.translationId,
        currentDeltaTranslation?.language
      );
      if (!showOnlyUpdatedParagraphs) {
        setDelta(result);
      } else {
        setDelta({
          ...result,
          rootElement: {
            ...result.rootElement,
            children: showOlyUpdatedDocumentElements(result.rootElement.children),
          },
        });
      }
      setIsLoadingDelta(false);
      setShouldBeRefresh(false);
    }
  }, [
    baseDetails,
    compareDetails,
    showOlyUpdatedDocumentElements,
    showOnlyUpdatedParagraphs,
    currentDeltaTranslation,
  ]);

  const handleShowOnlyChangedParagraphs = useCallback((value: boolean) => {
    setShowOnlyUpdatedParagraphs(value);
    setShouldBeRefresh(true);
  }, []);

  const withOnlyPotencialCandidates = useCallback(
    (elements: IDocumentElementDelta[]): IDocumentElementDelta[] =>
      elements
        .filter((elem) => {
          if (elem?.totalScore !== 0) {
            return true;
          }

          const hasChangedChildren = elem.children?.some(
            (child) => child?.totalScore !== 0 || withOnlyPotencialCandidates([child]).length > 0
          );

          return !!hasChangedChildren;
        })
        .map((elem) => ({
          ...elem,
          children: withOnlyPotencialCandidates(elem.children),
        })),
    []
  );

  const fetchLegalProvisionPlacementCandidates = useCallback(async () => {
    if (baseDetails && compareDetails && documentElementDetails) {
      setIsLoadingPlacementCandidates(true);
      const result = await legalProvisionService.getLegalProvisionPlacementCandidates(
        baseDetails.id,
        compareDetails.id,
        documentElementDetails.entityId
      );
      if (!showOnlyPotencialCandidates) {
        setLegalProvisionPlacementCandidates(result);
      } else {
        setLegalProvisionPlacementCandidates({
          ...result,
          rootElement: {
            ...result.rootElement,
            children: withOnlyPotencialCandidates(result.rootElement.children),
          },
        });
      }
      setIsLoadingPlacementCandidates(false);
      setShouldBeRefreshCandidates(false);
    }
  }, [
    baseDetails,
    compareDetails,
    documentElementDetails,
    showOnlyPotencialCandidates,
    withOnlyPotencialCandidates,
  ]);

  const handleShowOnlyPotencialCandidates = useCallback((value: boolean) => {
    setShowOnlyPotencialCandidates(value);
    setShouldBeRefreshCandidates(true);
  }, []);

  // Data context - only includes immutable data and fetch logic
  const dataValue = useMemo(
    () => ({
      baseDetails,
      setBaseDetails,
      compareDetails,
      setCompareDetails,
      isLoadingDelta,
      delta,
      fetchDeltaDetails,
      deltaTranslations,
      currentDeltaTranslation,
      setCurrentDeltaTranslation,
    }),
    [
      baseDetails,
      compareDetails,
      isLoadingDelta,
      delta,
      fetchDeltaDetails,
      deltaTranslations,
      currentDeltaTranslation,
    ]
  );

  // Document data context - only includes mutable UI state and handlers
  const dataDocumentValue = useMemo(
    () => ({
      documentElementDetails,
      setDocumentElementDetails,
      fetchDocumentElement,
      isLoadingPlacementCandidates,
      legalProvisionPlacementCandidates,
      fetchLegalProvisionPlacementCandidates,
    }),
    [
      documentElementDetails,
      setDocumentElementDetails,
      fetchDocumentElement,
      isLoadingPlacementCandidates,
      legalProvisionPlacementCandidates,
      fetchLegalProvisionPlacementCandidates,
    ]
  );

  // UI context - only includes UI state flags
  const uiValue = useMemo(
    () => ({
      showMoreDetails,
      setShowMoreDetails,
      showOnlyUpdatedParagraphs,
      handleShowOnlyChangedParagraphs,
      shouldBeRefresh,
      setShouldBeRefresh,
    }),
    [showMoreDetails, showOnlyUpdatedParagraphs, shouldBeRefresh, handleShowOnlyChangedParagraphs]
  );

  // UI document element context - only includes UI state flags for document element delta
  const uiDocumentElementValue = useMemo(
    () => ({
      showOnlyPotencialCandidates,
      handleShowOnlyPotencialCandidates,
      shouldBeRefreshCandidates,
      setShouldBeRefreshCandidates,
    }),
    [
      showOnlyPotencialCandidates,
      handleShowOnlyPotencialCandidates,
      shouldBeRefreshCandidates,
      setShouldBeRefreshCandidates,
    ]
  );

  useEffect(() => {
    const setContextFromURL = async (queryParams: URLSearchParams) => {
      // base
      const versionTimestamp = queryParams.get(BASE_HEADERS.VersionTimestamp);
      const releaseTimestamp = queryParams.get(BASE_HEADERS.ReleaseTimestamp);
      const entityId = queryParams.get(BASE_HEADERS.EntityId);
      const versionId = queryParams.get(BASE_HEADERS.VersionId);
      const id = queryParams.get(BASE_HEADERS.LegalProvisionId);

      if (id && entityId && versionId) {
        const baseLegalProvision = await getLegalProvisionDetails({
          id: id || '',
          entityId: entityId || '',
          versionId: versionId || '',
          versionTimestamp,
          releaseTimestamp,
        });
        setBaseDetails(baseLegalProvision);
      } else {
        setBaseDetails(null);
      }

      // compare
      const compareVersionTimestamp = queryParams.get(COMPARE_HEADERS.CompareVersionTimestamp);
      const compareReleaseTimestamp = queryParams.get(COMPARE_HEADERS.CompareReleaseTimestamp);
      const compareEntityId = queryParams.get(COMPARE_HEADERS.CompareEntityId);
      const compareVersionId = queryParams.get(COMPARE_HEADERS.CompareVersionId);
      const compareId = queryParams.get(COMPARE_HEADERS.CompareLegalProvisionId);

      if (compareEntityId && compareVersionId && compareId) {
        const compareLegalProvision = await getLegalProvisionDetails({
          id: compareId,
          entityId: compareEntityId,
          versionId: compareVersionId,
          versionTimestamp: compareVersionTimestamp,
          releaseTimestamp: compareReleaseTimestamp,
        });
        setCompareDetails(compareLegalProvision);
      } else {
        setCompareDetails(null);
      }
    };

    const setContext = () => {
      const queryParams = new URLSearchParams(window.location.search);
      if (!baseDetails && !compareDetails) {
        setContextFromURL(queryParams);
      }
    };

    setContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DeltaDataContext.Provider value={dataValue}>
      <DeltaDocumentDataContext.Provider value={dataDocumentValue}>
        <DeltaUIContext.Provider value={uiValue}>
          <DeltaDocumentUIContext.Provider value={uiDocumentElementValue}>
            {children}
          </DeltaDocumentUIContext.Provider>
        </DeltaUIContext.Provider>
      </DeltaDocumentDataContext.Provider>
    </DeltaDataContext.Provider>
  );
}
